import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const SignalManager = () => {
  const [signals, setSignals] = useState([]);
  const [formData, setFormData] = useState({
    coin_ticker: 'BTCUSDT',
    direction: 'LONG',
    leverage: 10,
    duration: 60,
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const coins = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'BNBUSDT'];

  useEffect(() => {
    fetchSignals();
  }, []);

  const fetchSignals = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/signals', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) setSignals(data.signals);
    } catch (error) {
      console.error('Failed to fetch signals:', error);
    }
  };

  const handleCreateSignal = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('http://localhost:5000/api/signals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Trading signal sent to users!');
        setFormData({ ...formData, message: '' });
        fetchSignals();
      } else {
        toast.error('Failed to send signal');
      }
    } catch (error) {
      toast.error('Failed to send signal');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      const response = await fetch(`http://localhost:5000/api/signals/${id}/deactivate`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Signal deactivated');
        fetchSignals();
      }
    } catch (error) {
      toast.error('Failed to deactivate signal');
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-white">Trading Signals</h1>
      
      <div className="card p-5">
        <h2 className="text-base font-semibold text-white mb-4">Send Trading Signal</h2>
        
        <form onSubmit={handleCreateSignal} className="space-y-4 max-w-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Coin</label>
              <select
                value={formData.coin_ticker}
                onChange={(e) => setFormData({ ...formData, coin_ticker: e.target.value })}
                className="input-dark w-full"
              >
                {coins.map(coin => (
                  <option key={coin} value={coin}>{coin}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Direction</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, direction: 'LONG' })}
                  className={`flex-1 py-2 rounded-lg text-sm transition ${
                    formData.direction === 'LONG' ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  LONG
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, direction: 'SHORT' })}
                  className={`flex-1 py-2 rounded-lg text-sm transition ${
                    formData.direction === 'SHORT' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  SHORT
                </button>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Leverage</label>
              <input
                type="number"
                value={formData.leverage}
                onChange={(e) => setFormData({ ...formData, leverage: Number(e.target.value) })}
                className="input-dark w-full"
                min="1"
                max="50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Duration (seconds)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                className="input-dark w-full"
                step="10"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1">Signal Message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="input-dark w-full h-20"
              placeholder="e.g., BTC showing strong bullish momentum. Enter LONG position."
              required
            />
          </div>
          
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? 'Sending...' : '📡 Send Signal to Users'}
          </button>
        </form>
      </div>
      
      <div className="card p-5">
        <h2 className="text-base font-semibold text-white mb-3">Signal History</h2>
        {signals.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No signals sent yet</p>
        ) : (
          <div className="space-y-2">
            {signals.map((signal) => (
              <div key={signal.id} className="flex items-center justify-between py-3 border-b border-gray-700">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-white">{signal.coin_ticker}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${signal.direction === 'LONG' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                      {signal.direction}
                    </span>
                    <span className="text-[10px] text-gray-500">{signal.leverage}x • {signal.duration}s</span>
                    {signal.is_active && <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-500">LIVE</span>}
                  </div>
                  <p className="text-xs text-gray-400">{signal.message}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{new Date(signal.created_at).toLocaleString()}</p>
                </div>
                {signal.is_active && (
                  <button
                    onClick={() => handleDeactivate(signal.id)}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Deactivate
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalManager;